import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Typography, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import UserMemberInfo from './UserMemberInfo';
import UserPI from './UserPI';
import UserProjects from './UserProjects';
import UserPermissions from './UserPermissions';
import UserHistory from './UserHistory';
import UserNotes from './UserNotes.tsx';
import { useApiGet } from '../../utils/httpClient';
import { getMyPermissions, getUserDetail } from '../../endpoints/usersTs';
import { roles } from '../../utils/variables';
import { permissionNames } from '../../utils/variables';

const UserDetail = () => {
  let { id } = useParams();
  const [tabSelected, setTabSelected] = useState('0');
  const [needToRefetch, setNeedToRefetch] = useState(false);

  const { data, isFetching, refetch } = useApiGet(
    ['user-detail', id],
    getUserDetail,
    {
      enabled: true,
    },
    {
      id: id,
    }
  );

  const { data: myPermissions = [] } = useApiGet(
    ['myPermissions-users-notes'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );

  const canSeeNotes = () => {
    return myPermissions.includes(permissionNames.UsersNotes);
  };

  const canSeePermissions = () => {
    let permissionRoles = [roles.Admin, roles.Support];
    let usersRole = localStorage.getItem('role');
    return permissionRoles.includes(usersRole);
  };

  const canSeeHistory = () => Boolean(data?.can_view_user_history);

  const canSeeMemberInfo = () => {
    return data?.full_view || data?.can_action;
  };

  if (isFetching) {
    return (
      <Grid container>
        <Grid item xs={12} padding textAlign={'center'}>
          <Typography variant="h4">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!data) {
    return (
      <Grid container>
        <Grid item xs={12} padding textAlign={'center'}>
          <Typography variant="h4">User not found</Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12} padding textAlign={'center'}>
        <Typography variant="h4">{data?.full_name}</Typography>
      </Grid>
      <Grid item xs={12} padding>
        <TabContext value={tabSelected}>
          <TabList
            value={tabSelected}
            onChange={(event, newValue) => setTabSelected(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Info" value={'0'} />
            {canSeeMemberInfo() && (
              <Tab label="Member Information" value={'1'} />
            )}
            {data?.has_project_history && <Tab label="Projects" value={'2'} />}
            {canSeePermissions() && <Tab label="Permissions" value={'3'} />}
            {canSeeNotes() && <Tab label="Notes" value={'4'} />}
            {canSeeHistory() && <Tab label="History" value={'5'} />}
          </TabList>
          <TabPanel value={'0'}>
            <UserPI
              id={id}
              refetchParent={refetch}
              canEdit={data?.can_edit}
              canAction={data?.can_action}
              canSetTempPassword={data?.can_set_temp_password}
              tabSelected={tabSelected}
              needToRefetch={needToRefetch}
              updateNeedToRefetch={setNeedToRefetch}
            />
          </TabPanel>
          {canSeeMemberInfo() && (
            <TabPanel value={'1'}>
              <UserMemberInfo
                id={id}
                canAction={data?.can_action}
                refetchParent={refetch}
                needToRefetch={needToRefetch}
                updateNeedToRefetch={setNeedToRefetch}
              />
            </TabPanel>
          )}
          <TabPanel value={'2'}>
            {data?.has_project_history && <UserProjects id={id} />}
          </TabPanel>
          <TabPanel value={'3'}>
            {canSeePermissions() && <UserPermissions id={id} />}
          </TabPanel>
          <TabPanel value={'4'}>
            {canSeeNotes() && <UserNotes id={id} />}
          </TabPanel>
          <TabPanel value={'5'}>
            {canSeeHistory() && <UserHistory id={id} />}
          </TabPanel>
        </TabContext>
      </Grid>
    </Grid>
  );
};

export default UserDetail;
